import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { IconAlertTriangle, IconCoins } from "@tabler/icons-react";

// Mantine's Textarea labels default to fw=500 (--mantine-fw-label).
// In the v2 form they read as shouty when stacked; nudge them down to 400.
const labelStyles = { label: { fontWeight: 400 } };
import { notifications } from "@mantine/notifications";
import { runPlugin } from "../api";
import { useStore } from "../store";
import SubjectBuilder from "./SubjectBuilder";
import { composeSubject } from "./subjectBuilderCatalog";
import { imageThumbUri } from "./imageUris";

/**
 * Form for running a manifest v2 plugin. Renders one field per declared input
 * (Text → Textarea / Choice → chip group, matching the legacy generator UX),
 * POSTs to `/api/plugins/{id}/run`, and refreshes the gallery so the new bundle
 * appears.
 *
 * Image inputs have NO field here — they follow the gallery selection (or the
 * explicit <c>sourceImageId</c> prop when rendered outside the gallery, e.g. on
 * the image details page), which the caller owns. They are resolved from
 * `selection` at submit time so no effect can ever wipe user-typed values.
 *
 * Exclusive-gate manifests (one Choice input whose options carry
 * estimatedCredits) drive everything: the selected gate value decides which
 * `modes`-tagged inputs/options are visible, what the cost badge says, and
 * whether a source image is demanded.
 */
export default function V2PluginRunForm({
  pluginId,
  options,
  onSubmitted,
  sourceImageId,
}) {
  const inputs = useMemo(() => options?.v2Inputs ?? [], [options]);
  const outputCount = options?.v2OutputCount ?? 1;
  const totalCost = options?.v2EstimatedCredits ?? 0;
  const buttonLabel = options?.v2RunButtonLabel || "Run plugin";

  const storeSelectedImages = useStore((state) => state.selectedImages);
  const setJobs = useStore((state) => state.setJobs);
  const galleryImages = useStore((state) => state.galleryImages);

  // Image inputs resolve from an explicit source (details page) when provided,
  // otherwise from the gallery selection the rail owns.
  const selection =
    sourceImageId != null
      ? sourceImageId
        ? [sourceImageId]
        : []
      : storeSelectedImages;

  const [values, setValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setValue = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  // The gate input is identified structurally: the Choice whose options carry
  // per-selection costs from the server. Its CURRENT value (default included)
  // drives visibility — deliberately not keyed on cost metadata, so a missing
  // cost projection can never hide option chips.
  const gateInput = useMemo(
    () => inputs.find((inp) => (inp.options || []).some((o) => o.estimatedCredits != null)),
    [inputs]
  );

  // Selection-adaptive gate options: a format that animates a source image is
  // pointless without a selection, and a text-to-image format ignores the
  // selection the user just made. Hide the inapplicable kind — but only when
  // the other kind remains, so source-only surfaces (video tab) keep their
  // format chips and guide via the "select an image" hint instead.
  const hasSelection = selection.length > 0;
  const eligibleGateOptions = useMemo(() => {
    const options = gateInput?.options ?? [];
    const sourceBased = options.filter((o) => o.requiresSourceImage);
    const textBased = options.filter((o) => !o.requiresSourceImage);
    if (hasSelection) return sourceBased.length > 0 ? sourceBased : options;
    return textBased.length > 0 ? textBased : options;
  }, [gateInput, hasSelection]);

  // Keep the stored gate value in sync with eligibility (e.g. selecting a gallery
  // image flips the image tab from Photo to Edit automatically).
  const currentGateValue = gateInput ? values[gateInput.name] : undefined;
  useEffect(() => {
    if (!gateInput || eligibleGateOptions.length === 0) return;
    const cur = currentGateValue ?? gateInput.default;
    if (!eligibleGateOptions.some((o) => o.value === cur)) {
      setValue(gateInput.name, eligibleGateOptions[0].value);
    }
  }, [gateInput, eligibleGateOptions, currentGateValue]);

  const gateValue = useMemo(() => {
    if (!gateInput) return undefined;
    const cur = values[gateInput.name] ?? gateInput.default;
    return eligibleGateOptions.some((o) => o.value === cur)
      ? cur
      : eligibleGateOptions[0]?.value;
  }, [gateInput, values, eligibleGateOptions]);
  const gateOption =
    gateValue != null ? gateInput?.options.find((o) => o.value === gateValue) : undefined;

  // Invocation templates: a mode option may declare named presets that override
  // the gated steps' guidance/steps/strength and LoRA scales. Selection is kept
  // per mode value, so switching between mode values never leaks one mode's pick
  // into another. With a single preset no picker is shown — the run just uses it.
  const [presetSelections, setPresetSelections] = useState({});
  const gatePresets = useMemo(() => gateOption?.presets ?? [], [gateOption]);
  const chosenPreset =
    gatePresets.find((p) => p.id === presetSelections?.[gateValue]) ?? gatePresets[0];
  useEffect(() => {
    if (gateValue == null || gatePresets.length === 0) return;
    setPresetSelections((prev) => {
      if (prev[gateValue] && gatePresets.some((p) => p.id === prev[gateValue]))
        return prev;
      return { ...prev, [gateValue]: gatePresets[0].id };
    });
  }, [gateValue, gatePresets]);
  const showPresetPicker = gatePresets.length > 1;
  const presetGroupLabel = gateOption?.presetsLabel || "Preset";

  // Platform-owned video settings. Only present when the selected gate format
  // targets a video model — the same Length/Quality/Aspect controls the plain
  // generator shows, rendered here so the plugin never hides them. Seeded from
  // the model's defaults, re-seeded only when the gate option changes.
  const videoModel = gateOption?.videoModel;
  const defaultDuration =
    gateOption?.videoDefaultDuration ?? videoModel?.allowedSeconds?.[0];
  const defaultAspect =
    gateOption?.videoDefaultAspectRatio ?? videoModel?.aspectRatios?.[0];
  const [videoDuration, setVideoDuration] = useState(null);
  const [videoResolution, setVideoResolution] = useState(null);
  const [videoAspect, setVideoAspect] = useState(null);
  const videoKey = gateOption?.value;
  const seededVideoRef = useRef(null);
  useEffect(() => {
    if (videoKey == null || seededVideoRef.current === videoKey) return;
    seededVideoRef.current = videoKey;
    setVideoDuration(defaultDuration ?? null);
    setVideoResolution(
      videoModel?.resolutions?.length > 1 ? videoModel.resolutions[0] : null
    );
    setVideoAspect(defaultAspect ?? null);
  }, [videoKey, defaultDuration, defaultAspect, videoModel?.resolutions]);

  const isVideoMode = !!videoModel;
  const videoRate =
    (videoModel?.resolutionCredits ?? {})[videoResolution] ??
    videoModel?.creditsPerSecond ??
    null;
  const badgeCost = isVideoMode
    ? Math.round((videoRate ?? 0) * Number(videoDuration ?? defaultDuration))
    : gateOption?.estimatedCredits ?? totalCost;

  const badgeOutputs = gateOption?.outputCount ?? outputCount;
  const needsSourceImage = !!gateOption?.requiresSourceImage;

  // A "plain filter" plugin: exactly one image input that the active format
  // consumes, image→image, single output. Selecting several gallery images then
  // runs in bulk — one derivative per source — instead of silently using only
  // the first. Multi-image-input plugins (e.g. reference + source pairs) keep
  // their pairing semantics.
  const imageInputs = useMemo(
    () => inputs.filter((inp) => inp.kind === "Image"),
    [inputs]
  );
  const isSingleSourceFilter =
    !isVideoMode && imageInputs.length === 1 && needsSourceImage && badgeOutputs === 1;
  const bulkCount = isSingleSourceFilter ? Math.max(selection.length, 1) : 1;
  const displayOutputs = badgeOutputs * bulkCount;
  const displayCost = badgeCost * bulkCount;

  // Author flagged the plugin as content-restricted (allows_nsfw) AND the
  // SELECTED format targets a strictly-moderated model (nano-banana, FAL MiniMax
  // H3). Per-selection, not per-plugin: one strict step in an unrelated mode
  // must not warn on every format. The model may refuse, but the run is still
  // charged — warn upfront rather than ambushing the user post-failure.
  const showModerationMismatchWarning =
    options?.allowsNsfw && !!gateOption?.strictModeration;

  // Click-together subject builder. Its composed string merges with the free-
  // text slot into one value at submit time — the builder rides ON TOP of a
  // plugin, it never replaces it: picking a plugin must not force the user to
  // express the whole subject as typed prompt.
  const [builderSelections, setBuilderSelections] = useState({});

  // Chip picks are plugin-scoped — reset when switching plugins so one
  // plugin's subject never leaks into another's run.
  useEffect(() => setBuilderSelections({}), [pluginId]);

  const subjectBuilder = !!options?.v2SubjectBuilder;
  const subjectInput = inputs.find((i) => i.name === "subject");
  const textInputs = inputs.filter((i) => i.kind === "Text");

  // Which free-text input receives the composed subject:
  //   - a plugin declaring subject_builder composes into its `subject` input;
  //   - otherwise a plain text-to-image plugin (no image inputs, a single
  //     free-text prompt) is composed the same way — the common shape where a
  //     bare prompt box would otherwise force everything into text.
  // Fixed-prompt recipes (no free-text slot) and image-fed plugins keep their
  // authored form untouched.
  const composerSlot =
    (subjectBuilder ? subjectInput : null) ??
    (!subjectBuilder && imageInputs.length === 0 && textInputs.length === 1
      ? textInputs[0]
      : null);

  // The composer only applies while the slot's mode is active: recipes whose
  // active mode doesn't consume the free text must not offer controls that do
  // nothing.
  //
  // Image-input video modes (I2V) never compose a subject: the source image
  // already IS the subject, so the free text must describe the motion/change.
  // The chips would fight the start frame — suppress them there.
  const composerActive =
    !!composerSlot &&
    !(isVideoMode && needsSourceImage) &&
    (!composerSlot.modes?.length ||
      (gateValue != null && composerSlot.modes.includes(gateValue)));

  // The slot's effective value: chips + whatever sits in the free-text box.
  const composedSubjectValue = useMemo(() => {
    if (!composerActive || !composerSlot) return null;
    const built = composeSubject(builderSelections);
    const extra = String(values[composerSlot.name] || "").trim();
    return [built, extra].filter(Boolean).join(", ");
  }, [composerActive, composerSlot, builderSelections, values]);

  // Gate-value visibility: inputs/options tagged with `modes` only apply to
  // specific selections (e.g. engine-specific action LoRAs). Until a gate value
  // is chosen, mode-dependent pieces stay hidden. Image-kind slots follow the
  // selected format's source demand rather than a modes tag — they exist to
  // feed formats that animate an image, so they're hidden otherwise.
  const visibleInputs = useMemo(
    () =>
      inputs.filter((inp) => {
        if (inp.kind === "Image") return needsSourceImage;
        // With the composer active, the raw free-text slot is replaced by the
        // builder + its free-text box.
        if (composerSlot && composerActive && inp.name === composerSlot.name)
          return false;
        return (
          !inp.modes?.length || (gateValue != null && inp.modes.includes(gateValue))
        );
      }),
    [inputs, gateValue, needsSourceImage, composerSlot, composerActive]
  );

  // Prune multi-select choices that the newly-selected gate value doesn't offer
  // (e.g. a W22-only action left over after switching to photo mode).
  useEffect(() => {
    if (gateValue == null) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const inp of inputs) {
        if (inp.kind !== "Choice" || !inp.multiSelect) continue;
        const cur = prev[inp.name];
        if (!Array.isArray(cur)) continue;
        const kept = cur.filter((v) => {
          const o = (inp.options || []).find((opt) => opt.value === v);
          return !o?.modes?.length || o.modes.includes(gateValue);
        });
        if (kept.length !== cur.length) {
          next[inp.name] = kept;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [gateValue, inputs]);

  // Guards the defaults seeding against options refetches: keyed on the plugin
  // id, not the inputs array identity, so a fresh options object can't reset
  // what the user already picked/typed.
  const seededPluginRef = useRef(null);

  useEffect(() => {
    if (seededPluginRef.current === pluginId) return;
    seededPluginRef.current = pluginId;
    const seeded = {};
    for (const inp of inputs) {
      if (inp.kind === "Image") continue;
      if (inp.multiSelect) {
        seeded[inp.name] = inp.defaults ?? (inp.default ? [inp.default] : []);
      } else if (inp.default) {
        seeded[inp.name] = inp.default;
      }
    }
    setValues(seeded);
  }, [pluginId, inputs]);

  // i-th image input takes the i-th selected image (falls back to the first).
  const resolveImageValues = () => {
    const resolved = {};
    let imgIdx = 0;
    for (const inp of inputs) {
      if (inp.kind !== "Image") continue;
      resolved[inp.name] = selection[imgIdx] ?? selection[0] ?? "";
      imgIdx++;
    }
    return resolved;
  };

  const allValues = () => ({ ...values, ...resolveImageValues() });

  const isEmpty = (inp, v) =>
    inp.multiSelect ? !Array.isArray(v) || v.length === 0 : !v;

  const missingRequiredImage = visibleInputs.find(
    (inp) =>
      inp.kind === "Image" &&
      inp.required &&
      isEmpty(inp, allValues()[inp.name])
  );
  const missingSourceForMode = needsSourceImage && selection.length === 0;
  // The composer slot is hidden from visibleInputs, so its requiredness is
  // judged on the composed value (chips + free text) instead of the raw state.
  const composerMissing =
    composerActive &&
    !!composerSlot?.required &&
    !String(composedSubjectValue || "").trim();
  const hasMissingRequired =
    missingSourceForMode ||
    visibleInputs.some((inp) => inp.required && isEmpty(inp, allValues()[inp.name])) ||
    composerMissing;

  // The raw prompt the customer actually typed — never the chips the plugin
  // composed. Sent with the run so outputs carry a Prompt row in the editor only
  // when the text is genuine. Composer slots: the free-text box is the only typed
  // surface (chips never count, an untouched seeded default doesn't either).
  // Other plugins: a single free-text input (rendered as "Prompt") counts unless
  // it's still the untouched default.
  const computeTypedPrompt = () => {
    if (composerActive && composerSlot) {
      const raw = String(values[composerSlot.name] || "").trim();
      if (composerSlot.default && raw === composerSlot.default) return undefined;
      return raw || undefined;
    }
    const textFields = visibleInputs.filter((i) => i.kind === "Text");
    if (textFields.length !== 1) return undefined;
    const inp = textFields[0];
    const raw = typeof values[inp.name] === "string" ? (values[inp.name] || "").trim() : "";
    if (!raw) return undefined;
    if (inp.default && raw === inp.default) return undefined;
    return raw;
  };

  const submit = async () => {
    // Required-field guard. The backend re-validates; this just gives instant
    // feedback so the user doesn't pay a roundtrip to learn they missed a slot.
    if (missingSourceForMode) {
      notifications.show({
        color: "red",
        title: "Source image needed",
        message: "Select an image in the gallery — this format animates a source image.",
      });
      return;
    }
    const missing = visibleInputs.filter(
      (inp) => inp.required && isEmpty(inp, allValues()[inp.name])
    );
    if (composerActive && composerSlot?.required && !String(composedSubjectValue || "").trim()) {
      missing.push(composerSlot);
    }
    if (missing.length > 0) {
      for (const inp of missing) {
        notifications.show({
          color: "red",
          title: "Missing input",
          message: `"${inp.label || inp.name}" is required.`,
        });
      }
      return;
    }
    setIsSubmitting(true);
    const composed = composedSubjectValue;
    const overrides = isVideoMode
      ? {
          duration: Number(videoDuration),
          ...(videoResolution ? { resolution: videoResolution } : {}),
          ...(videoAspect ? { aspectRatio: videoAspect } : {}),
        }
      : {};
    // Chosen invocation template for the active mode (only sent when the mode
    // actually offers a pickable set).
    const presetsForGate =
      gateValue != null && gateInput && gatePresets.length > 1 && chosenPreset
        ? { [gateInput.name]: chosenPreset.id }
        : {};
    // One POST per source image when running a plain filter in bulk; a single
    // POST otherwise. Sequential so an out-of-credits failure stops cleanly
    // instead of silently charging a partial batch mid-flight.
    // Each POST's fan-out jobs are added to jobs[] right away (the poll loop
    // reconciles with the server) so processing cards appear with the source
    // image as their backdrop.
    const registerJobs = (jobIds, imgId) => {
      if (!jobIds?.length) return;
      const uri =
        imageThumbUri(
          galleryImages,
          imgId != null ? imgId : selection[0] ?? null
        ) ?? undefined;
      const cards = jobIds.map((jobId) => ({
        jobId,
        status: "Queued",
        uri,
        clientId: Math.random().toString(36).substr(2, 9),
      }));
      setJobs((js) => {
        const known = new Set(js.map((j) => j.jobId));
        return [...cards.filter((c) => !known.has(c.jobId)), ...js];
      });
    };
    const submitOne = async (imgId) => {
      const base =
        imgId != null && imageInputs.length > 0
          ? { ...values, [imageInputs[0].name]: imgId }
          : allValues();
      const payload =
        composed != null && composerSlot
          ? { ...base, [composerSlot.name]: composed }
          : base;
      const res = await runPlugin(
        pluginId,
        payload,
        overrides,
        presetsForGate,
        computeTypedPrompt()
      );
      registerJobs(res?.jobIds, imgId);
    };
    let queued = 0;
    try {
      if (isSingleSourceFilter) {
        for (const imgId of selection) {
          await submitOne(imgId);
          queued += 1;
        }
      } else {
        await submitOne(null);
        queued = 1;
      }
      const shownOutputs = badgeOutputs * queued;
      notifications.show({
        color: "green",
        title: "Plugin run started",
        message: `${shownOutputs} output${shownOutputs === 1 ? "" : "s"} queued (${
          badgeCost * shownOutputs
        } credits) — they'll appear in your gallery as they finish.`,
      });
      if (onSubmitted) onSubmitted();
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Plugin run failed",
        message:
          queued > 0
            ? `${queued} of ${bulkCount} runs queued, then stopped: ${err.message}`
            : err.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap="sm">
      {/* The mode gate (e.g. "Format") must not be displaced by the subject
          chips: its value decides which selector options apply, so it leads
          the form whenever the composer is active. Without a composer the gate
          keeps its authored position among the other choices below. */}
      {composerActive && gateInput && (
        <ChoiceField
          key={gateInput.name}
          input={{ ...gateInput, options: eligibleGateOptions }}
          gateValue={gateValue}
          value={values[gateInput.name]}
          onChange={(v) => setValue(gateInput.name, v)}
        />
      )}

      {composerActive && composerSlot && (
        <>
          <SubjectBuilder onChange={setBuilderSelections} />
          <Textarea
            label="Extra details"
            placeholder="Anything the chips don't cover"
            value={
              typeof values[composerSlot.name] === "string"
                ? values[composerSlot.name]
                : ""
            }
            onChange={(evt) => setValue(composerSlot.name, evt.currentTarget.value)}
            autosize
            minRows={1}
            maxRows={3}
            styles={labelStyles}
          />
        </>
      )}

      {visibleInputs
        .filter(
          (inp) =>
            inp.kind === "Choice" && !(composerActive && inp === gateInput)
        )
        .map((inp) => (
          <ChoiceField
            key={inp.name}
            input={inp === gateInput ? { ...inp, options: eligibleGateOptions } : inp}
            gateValue={gateValue}
            value={values[inp.name]}
            onChange={(v) => setValue(inp.name, v)}
          />
        ))}

      {showPresetPicker && (
        <Box>
          {chosenPreset?.previewImageUrl && (
            <Box mb={6} style={{ display: "flex", justifyContent: "center" }}>
              <img
                src={chosenPreset.previewImageUrl}
                alt={chosenPreset.label}
                style={{
                  maxHeight: 200,
                  maxWidth: "100%",
                  borderRadius: 8,
                  objectFit: "cover",
                }}
              />
            </Box>
          )}
          <PresetPicker
            label={presetGroupLabel}
            presets={gatePresets}
            value={chosenPreset?.id ?? gatePresets[0]?.id}
            onChange={(id) => {
              if (gateValue != null)
                setPresetSelections((prev) => ({ ...prev, [gateValue]: id }));
            }}
          />
        </Box>
      )}

      {isVideoMode && (
        <Stack gap={8} mt={4}>
          <Text fz="sm" styles={{ root: { fontWeight: 400 } }}>
            Video settings
          </Text>
          <Group grow align="flex-end">
            <Select
              size="xs"
              label="Length"
              value={videoDuration == null ? null : String(videoDuration)}
              onChange={(v) => setVideoDuration(Number(v))}
              data={(videoModel?.allowedSeconds ?? []).map((s) => ({
                label: `${s}s`,
                value: String(s),
              }))}
              allowDeselect={false}
            />
            {(videoModel?.resolutions?.length ?? 0) > 1 && (
              <Select
                size="xs"
                label="Quality"
                value={videoResolution}
                onChange={setVideoResolution}
                data={(videoModel?.resolutions ?? []).map((r) => ({
                  label: r === "2k" ? "2K (best)" : "768p (standard)",
                  value: r,
                }))}
                allowDeselect={false}
              />
            )}
          </Group>
          {(videoModel?.aspectRatios?.length ?? 0) > 0 && (
            <>
              <Text fz="sm" styles={{ root: { fontWeight: 400 } }}>
                Aspect ratio
              </Text>
              <Chip.Group value={videoAspect} onChange={setVideoAspect}>
                <Group gap={6}>
                  {(videoModel?.aspectRatios ?? []).map((ar) => (
                    <Chip key={ar} value={ar} size="xs" variant="light">
                      {ar}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </>
          )}
        </Stack>
      )}

      {visibleInputs
        .filter((inp) => inp.kind === "Text")
        .map((inp) => (
          <Textarea
            key={inp.name}
            // A plugin's single free-text input is presented as "Prompt" —
            // the term users know from every other generator mode — no matter
            // what the manifest calls it. Multi-input plugins keep their
            // authored labels (e.g. "Subject", "Vibe") since they disambiguate.
            label={
              visibleInputs.filter((i) => i.kind === "Text").length === 1
                ? "Prompt"
                : inp.label || inp.name
            }
            value={typeof values[inp.name] === "string" ? values[inp.name] : ""}
            onChange={(evt) => setValue(inp.name, evt.currentTarget.value)}
            required={inp.required}
            autosize
            minRows={2}
            maxRows={5}
            styles={labelStyles}
          />
        ))}

      {(missingRequiredImage || missingSourceForMode) && (
        <Text ta="center" fz="xs" c="dimmed">
          Select an input image in the gallery
        </Text>
      )}

      {showModerationMismatchWarning && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="This plugin uses a strictly-moderated model"
        >
          One of the steps uses a model that refuses content it classifies as
          adult-only. If your prompt or input images trigger its filter, the run
          will fail — and credits will still be charged.
        </Alert>
      )}

      <Button
        mt="md"
        type="button"
        loading={isSubmitting}
        disabled={hasMissingRequired}
        onClick={submit}
      >
        {buttonLabel}
      </Button>
      <Group justify="center" gap="xs" mt={-4}>
        <Badge
          variant="light"
          color="gray"
          leftSection={<IconCoins stroke={1.8} size={12} />}
        >
          {displayCost} credits · {displayOutputs}{" "}
          {displayOutputs === 1 ? "output" : "outputs"}
        </Badge>
      </Group>
      {bulkCount > 1 && (
        <Text ta="center" fz="xs" c="dimmed" mt={-2}>
          One per selected image — {bulkCount} sources selected.
        </Text>
      )}
    </Stack>
  );
}

/**
 * Choice inputs render as chip groups — same interaction as the legacy
 * generator, no dropdowns. Options tagged with `modes` only appear for the
 * matching gate selection. Long lists get a filter box.
 */
function PresetPicker({ presets, label, value, onChange }) {
  return (
    <Stack gap={6}>
      <Text fz="sm" styles={{ root: { fontWeight: 400 } }}>
        {label}
      </Text>
      <Chip.Group value={value} onChange={onChange}>
        <Group gap={6}>
          {presets.map((p) => (
            <Chip key={p.id} value={p.id} size="xs" variant="light">
              {p.label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Stack>
  );
}

/**
 * Choice inputs render as chip groups — same interaction as the legacy
 * generator, no dropdowns. Options tagged with `modes` only appear for the
 * matching gate selection. Long lists get a filter box.
 */
function ChoiceField({ input, value, onChange, gateValue }) {
  const [filter, setFilter] = useState("");
  const isMulti = !!input.multiSelect;
  const label = input.label || input.name;

  const allOptions = input.options || [];
  const visibleCount = allOptions.filter(
    (o) => !o.modes?.length || (gateValue != null && o.modes.includes(gateValue))
  ).length;
  const options = allOptions
    .filter((o) => !o.modes?.length || (gateValue != null && o.modes.includes(gateValue)))
    .filter((o) => !filter || o.label.toLowerCase().includes(filter.toLowerCase()));

  const handleChange = (next) => {
    if (
      isMulti &&
      input.maxSelect &&
      Array.isArray(next) &&
      next.length > input.maxSelect
    )
      return; // silently ignore picks beyond the cap — chips stay unchecked
    onChange(next || undefined);
  };

  return (
    <Stack gap={6}>
      <Text fz="sm" styles={{ root: { fontWeight: 400 } }}>
        {label}
        {input.required && (
          <span style={{ color: "var(--mantine-color-red-6)", marginLeft: 4 }}>
            *
          </span>
        )}
      </Text>
      {visibleCount > 10 && (
        <TextInput
          size="xs"
          placeholder="Filter…"
          value={filter}
          onChange={(evt) => setFilter(evt.currentTarget.value)}
        />
      )}
      <Chip.Group multiple={isMulti} value={value ?? (isMulti ? [] : null)} onChange={handleChange}>
        <Group gap={6}>
          {options.map((o) => (
            <Chip key={o.value} value={o.value} size="xs" variant="light">
              {o.previewImageUrl && (
                <img
                  src={o.previewImageUrl}
                  alt=""
                  style={{
                    width: 18,
                    height: 18,
                    objectFit: "cover",
                    borderRadius: 3,
                    marginRight: 6,
                    verticalAlign: "middle",
                    display: "inline-block",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              {o.label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Stack>
  );
}
